import React from 'react';
import { Box, Skeleton, Paper } from '@mui/material';

// Represents a row of standard Dashboard cards (used for Most Active, Top Movers, Yearly High/Low)
export const HorizontalCardSkeleton = ({ count = 5 }) => {
  return (
    <Box sx={{ display: 'flex', gap: 2, overflowX: 'hidden', py: 1, mb: 2 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Paper
          key={index}
          elevation={0}
          sx={{
            minWidth: 220,
            maxWidth: 240,
            flex: '0 0 auto',
            borderRadius: 2,
            border: '1px solid #E0E0E0',
            p: 2,
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: 110
          }}
        >
          {/* Top Row: Logo & Name */}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Skeleton animation="wave" variant="rectangular" width={40} height={40} sx={{ borderRadius: 1 }} />
            <Box sx={{ overflow: 'hidden', flex: 1 }}>
              <Skeleton animation="wave" variant="text" width="80%" height={20} />
              <Skeleton animation="wave" variant="text" width="60%" height={16} />
            </Box>
          </Box>
          
          {/* Bottom Row: Price & Change */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
            <Skeleton animation="wave" variant="text" width="40%" height={24} />
            <Skeleton animation="wave" variant="text" width="30%" height={20} />
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

// Represents list rows (used for Option Chain, Indices list, etc.)
export const ListRowSkeleton = ({ count = 6, rowHeight = 56 }) => {
  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Paper
          key={index}
          elevation={0}
          sx={{
            width: '100%',
            height: rowHeight,
            mb: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '40%' }}>
            <Skeleton animation="wave" variant="circular" width={28} height={28} />
            <Skeleton animation="wave" variant="text" width="60%" height={24} />
          </Box>
          <Box sx={{ display: 'flex', gap: 3, width: '40%', justifyContent: 'flex-end' }}>
            <Skeleton animation="wave" variant="text" width="40%" height={24} />
            <Skeleton animation="wave" variant="text" width="40%" height={24} />
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

// Represents a flat table row without outer borders (used for Top Movers, Yearly High/Low)
export const TableRowSkeleton = ({ count = 5 }) => {
  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1.8,
            borderBottom: '1px solid #f0f0f0',
            '&:last-child': { borderBottom: 'none' }
          }}
        >
          {/* Column 1: Scrip Name */}
          <Box sx={{ flex: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Skeleton animation="wave" variant="text" width="40%" height={20} />
            <Skeleton animation="wave" variant="text" width="60%" height={16} />
          </Box>

          {/* Column 2: LTP */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', px: 2 }}>
            <Skeleton animation="wave" variant="text" width="60%" height={24} />
          </Box>

          {/* Column 3: Change */}
          <Box sx={{ flex: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Skeleton animation="wave" variant="text" width="50%" height={20} />
            <Skeleton animation="wave" variant="text" width="40%" height={16} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};
